<script lang="ts">
  import { CATEGORY_META, TIMINGS, categoryLabel, type Category, type DisplayView } from '@banger/shared';
  import { trackHeading } from '../lib/trackCopy';
  import Avatar from '../shared/Avatar.svelte';
  import TimerRing from '../shared/TimerRing.svelte';

  let { view, secs }: { view: DisplayView; secs: number } = $props();

  const PROMPT: Record<Category, string> = {
    title: 'Name that song!',
    artist: "Who's singing?",
    decade: 'What decade?',
    before2000: 'Before or after?',
    year4: 'Guess the year (±4)',
  };

  const meta = $derived(view.category ? CATEGORY_META[view.category] : null);
  // Dealt-in players answer; during a tie-break only the tied players do.
  const answerers = $derived(
    view.players.filter((p) => p.squaresToGoal !== null && (!view.tiebreak || view.tiebreak.includes(p.id))),
  );
  const locked = $derived(answerers.filter((p) => p.lockedIn).length);
  const tiedNames = $derived(
    view.tiebreak ? view.players.filter((p) => view.tiebreak!.includes(p.id)).map((p) => p.name).join(' vs ') : '',
  );
</script>

<main style="--c:{meta?.color ?? '#fff'}">
  <header>
    {#if meta}<span class="pill">{categoryLabel(view.category!, view.cutoffYear)}</span>{/if}
    <p class="prompt">{view.category === 'before2000' ? `Before ${view.cutoffYear ?? 2000} or after?` : view.category ? PROMPT[view.category] : ''}</p>
    {#if view.tiebreak}<p class="tb neon">TIEBREAK! {tiedNames} — first correct answer wins</p>{/if}
  </header>

  <div class="center">
    <div class="eq" aria-hidden="true">{#each Array(7) as _, i}<span style="--i:{i}"></span>{/each}</div>
    <TimerRing {secs} total={TIMINGS.answerMs / 1000} size="clamp(150px, 26vh, 280px)" hurry={secs <= 5 && !view.paused} caption="seconds left" />
    <div class="eq" aria-hidden="true">{#each Array(7) as _, i}<span style="--i:{6 - i}"></span>{/each}</div>
  </div>

  <section>
    <ul class="players">
      {#each answerers as p (p.id)}
        <li class:locked={p.lockedIn} class:away={!p.connected}>
          <span class="av"><Avatar emoji={p.emoji} size={84} />{#if p.lockedIn}<span class="tick">✅</span>{/if}</span>
          <span class="name">{p.name}</span>
        </li>
      {/each}
    </ul>
    <p class="count">{locked} of {answerers.length} locked in</p>
  </section>
  <p class="meta">{trackHeading(view)}</p>
</main>

<style>
  main { min-height: 100%; display: grid; grid-template-rows: auto 1fr auto auto; justify-items: center; align-items: center; gap: 2vh; padding: 4vh 4vw 3vh; text-align: center; }
  header { display: grid; justify-items: center; gap: 1vh; }
  .pill {
    font-size: clamp(1.6rem, 3.4vw, 3rem); font-weight: 900; color: #0a0618; background: var(--c);
    padding: 0.2em 1em; border-radius: 999px; letter-spacing: 0.04em; box-shadow: 0 0 24px var(--c), 0 0 60px var(--c);
  }
  /* The question is the headline; the timer below must never read as an answer. */
  .prompt {
    margin: 0; font-size: clamp(2.6rem, 6.5vw, 6.5rem); font-weight: 900; line-height: 1.05; color: #fff;
    text-shadow: 0 0 14px var(--c), 0 0 40px color-mix(in srgb, var(--c) 60%, transparent);
  }
  .tb { margin: 0; font-size: clamp(1.4rem, 2.8vw, 2.4rem); font-weight: 900; }
  .center { display: flex; align-items: center; gap: 4vw; }
  .eq { display: flex; align-items: center; gap: 8px; height: 18vh; }
  .eq span {
    width: clamp(8px, 1.2vw, 18px); height: 100%; border-radius: 99px; transform-origin: center;
    background: linear-gradient(var(--pink), var(--cyan));
    animation: eq 0.9s ease-in-out calc(var(--i) * -0.13s) infinite alternate;
  }
  .players { list-style: none; margin: 0; padding: 0; display: flex; flex-wrap: wrap; justify-content: center; gap: 1.5vw 2.5vw; }
  li { display: grid; justify-items: center; gap: 6px; opacity: 0.55; transition: opacity 0.3s; }
  li.locked { opacity: 1; }
  li.away { filter: grayscale(1); }
  .av { position: relative; display: inline-flex; }
  .locked .av { animation: pop 0.35s ease-out; }
  .tick { position: absolute; right: -10px; bottom: -6px; font-size: 2.2rem; filter: drop-shadow(0 0 6px #69db7c); }
  .name { font-size: clamp(1.1rem, 1.8vw, 1.6rem); font-weight: 700; max-width: 10ch; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .count { margin: 1vh 0 0; font-size: clamp(1.2rem, 2.2vw, 2rem); opacity: 0.8; }
  .meta { margin: 0; font-size: clamp(1rem, 1.6vw, 1.4rem); opacity: 0.6; }
  @keyframes eq { from { transform: scaleY(0.15); } to { transform: scaleY(1); } }
  @keyframes pop { 50% { transform: scale(1.25); } }
</style>
